import React, { useState } from 'react';
import { Alert, Button, Snackbar, Stack, TextField } from '@mui/material';
import { useAuth } from './AuthContext';

const VerifyEmailBanner = () => {
  const { user, resendVerification, verifyEmailCode } = useAuth();
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [code, setCode] = useState('');
  const [toast, setToast] = useState('');
  const [error, setError] = useState('');

  if (!user || user.emailVerified || user.provider !== 'local') {
    return null;
  }

  const handleResend = async () => {
    setSending(true);
    setError('');
    try {
      await resendVerification();
      setToast('Verification email sent.');
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Unable to resend verification email.',
      );
    } finally {
      setSending(false);
    }
  };

  const handleVerify = async (event: React.FormEvent) => {
    event.preventDefault();
    const trimmed = code.trim();
    if (!/^\d{6}$/.test(trimmed)) {
      setError('Enter the 6-digit code from your email.');
      return;
    }

    setVerifying(true);
    setError('');
    try {
      await verifyEmailCode(trimmed);
      setToast('Email verified.');
      setCode('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to verify email.');
    } finally {
      setVerifying(false);
    }
  };

  return (
    <>
      <Alert
        severity="warning"
        sx={{
          borderRadius: 0,
          justifyContent: 'center',
          '& .MuiAlert-message': {
            width: '100%',
          },
        }}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          justifyContent="space-between"
          sx={{ width: '100%' }}
        >
          <span>
            Confirm {user.email} to start your free trial. Check your inbox and junk
            folder for a 6-digit code.
          </span>
          <Stack
            component="form"
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            alignItems="center"
            onSubmit={(event) => void handleVerify(event)}
          >
            <TextField
              size="small"
              label="6-digit code"
              value={code}
              onChange={(event) => {
                setCode(event.target.value.replace(/\D/g, '').slice(0, 6));
                setError('');
              }}
              inputProps={{
                inputMode: 'numeric',
                pattern: '[0-9]*',
                maxLength: 6,
                'aria-label': 'Verification code',
              }}
              sx={{ minWidth: 140, bgcolor: 'background.paper' }}
            />
            <Button
              type="submit"
              color="inherit"
              variant="outlined"
              size="small"
              disabled={verifying || code.length !== 6}
            >
              {verifying ? 'Verifying…' : 'Verify'}
            </Button>
            <Button
              color="inherit"
              size="small"
              disabled={sending}
              onClick={() => void handleResend()}
            >
              {sending ? 'Sending…' : 'Resend email'}
            </Button>
          </Stack>
        </Stack>
      </Alert>
      {error && (
        <Alert severity="error" sx={{ borderRadius: 0 }}>
          {error}
        </Alert>
      )}
      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={4000}
        onClose={() => setToast('')}
        message={toast}
      />
    </>
  );
};

export default VerifyEmailBanner;
